/**
 * Buscador de distribuidores (PDP/página institucional).
 * Dados vêm de metaobjects via JSON embutido na section.
 */
class DistributorFinder extends HTMLElement {
  static BRAZIL_STATES = [
    'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO',
    'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR',
    'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
  ];

  static STATE_LABELS = {
    AC: 'Acre', AL: 'Alagoas', AM: 'Amazonas', AP: 'Amapá', BA: 'Bahia',
    CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás',
    MA: 'Maranhão', MG: 'Minas Gerais', MS: 'Mato Grosso do Sul', MT: 'Mato Grosso',
    PA: 'Pará', PB: 'Paraíba', PE: 'Pernambuco', PI: 'Piauí', PR: 'Paraná',
    RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RO: 'Rondônia', RR: 'Roraima',
    RS: 'Rio Grande do Sul', SC: 'Santa Catarina', SE: 'Sergipe', SP: 'São Paulo',
    TO: 'Tocantins',
  };

  // Quantidade de cartões renderizados por vez. Renderizar todos de uma só
  // vez (90+ cartões com ícones SVG embutidos) derrubava o Safari do iPhone.
  static BATCH_SIZE = 12;

  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.i18n = this.readJson('[data-i18n]', {});
    this.data = this.normalizeData(this.readJson('[data-distributors]', []));

    this.countrySelect = this.querySelector('[data-country-select]');
    this.stateSelect = this.querySelector('[data-state-select]');
    this.cardsContainer = this.querySelector('[data-cards]');
    this.mapContainer = this.querySelector('[data-map]');
    this.noMapEl = this.querySelector('[data-no-map]');
    this.statePaths = this.querySelectorAll('.state-path[data-state]');
    this.svgMapEl = this.querySelector('[data-map-svg]');
    this.svgStates = [];
    this.pending = [];
    this.renderedCount = 0;

    this.populateCountrySelect();
    this.bindEvents();
    this.loadSvgMap();
    this.applyUrlParams();
  }

  // Mapa mobile: SVG com os estados como <path> de verdade — carregado sob
  // demanda (fetch) pra não pesar no HTML da página em telas maiores, onde
  // ele fica escondido via CSS.
  loadSvgMap() {
    if (!this.svgMapEl) return;
    const url = this.svgMapEl.dataset.svgUrl;
    if (!url) return;

    fetch(url)
      .then((response) => response.text())
      .then((markup) => {
        // innerHTML com uma string crua às vezes não entra em "modo SVG" no
        // parser (o navegador trata <style>/<title>/<text> como texto solto
        // em vez de desenhar o mapa). DOMParser força o parse como SVG de
        // verdade, com o namespace correto.
        const svgEl = new DOMParser().parseFromString(markup, 'image/svg+xml').documentElement;
        if (!svgEl || svgEl.nodeName.toLowerCase() !== 'svg') return;

        this.svgMapEl.innerHTML = '';
        this.svgMapEl.appendChild(document.importNode(svgEl, true));
        this.svgStates = Array.from(this.svgMapEl.querySelectorAll('.estado[data-uf]'));
        this.bindSvgEvents();
        this.updateMapHighlight();
      })
      .catch(() => {
        // Falhou o fetch (ex.: offline) — mapa mobile simplesmente não aparece,
        // os selects continuam funcionando normalmente.
      });
  }

  bindSvgEvents() {
    this.svgStates.forEach((state) => {
      const uf = state.dataset.uf;
      state.addEventListener('mouseenter', () => state.classList.add('is-hover'));
      state.addEventListener('mouseleave', () => state.classList.remove('is-hover'));
      state.addEventListener('click', (event) => {
        event.preventDefault();
        this.stateSelect.value = uf;
        this.updateMapHighlight();
        this.renderCards();
        this.pushUrl();
      });
    });
  }

  readJson(selector, fallback) {
    const el = this.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return fallback;
    }
  }

  comparePt(a, b) {
    return String(a || '').localeCompare(String(b || ''), 'pt', { sensitivity: 'base' });
  }

  sortEntries(entries) {
    return [...entries].sort((a, b) => {
      const byCompany = this.comparePt(a.company, b.company);
      if (byCompany !== 0) return byCompany;
      return this.comparePt(a.city, b.city);
    });
  }

  normalizeData(raw) {
    const items = Array.isArray(raw) ? raw : [];
    const grouped = {};

    items.forEach((item) => {
      if (!item?.company) return;

      const country = (item.country || '').trim();
      const state = (item.state || '').trim();
      const isBrazil = !country || country === 'Brasil';
      const key = isBrazil && /^[A-Z]{2}$/.test(state) ? state : 'Sem_Estado';

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    });

    Object.keys(grouped).forEach((key) => {
      grouped[key] = this.sortEntries(grouped[key]);
    });

    return grouped;
  }

  populateCountrySelect() {
    const countries = this.getCountries();
    const current = this.countrySelect.value;
    this.countrySelect.innerHTML = '';

    countries.forEach((country) => {
      const option = document.createElement('option');
      option.value = country;
      option.textContent = country;
      this.countrySelect.appendChild(option);
    });

    if (current && countries.includes(current)) {
      this.countrySelect.value = current;
    } else if (countries.includes('Brasil')) {
      this.countrySelect.value = 'Brasil';
    }
  }

  getCountries() {
    const countries = new Set();
    Object.values(this.data).forEach((entries) => {
      entries.forEach((entry) => {
        if (entry.country) countries.add(entry.country);
      });
    });

    return Array.from(countries).sort((a, b) => {
      if (a === 'Brasil') return -1;
      if (b === 'Brasil') return 1;
      return this.comparePt(a, b);
    });
  }

  getStatesForCountry(country) {
    if (country === 'Brasil') return [...DistributorFinder.BRAZIL_STATES];

    const states = new Set();
    (this.data.Sem_Estado || []).forEach((entry) => {
      if (entry.country === country && entry.state) states.add(entry.state);
    });
    return Array.from(states).sort((a, b) => this.comparePt(a, b));
  }

  getDistributorsForSelection(country, state) {
    if (country === 'Brasil') {
      if (state) return this.data[state] || [];

      return this.sortEntries(
        Object.keys(this.data)
          .filter((key) => key !== 'Sem_Estado')
          .flatMap((key) => this.data[key] || [])
      );
    }

    return this.sortEntries(
      (this.data.Sem_Estado || []).filter((entry) => {
        if (entry.country !== country) return false;
        if (state && entry.state !== state) return false;
        return true;
      })
    );
  }

  applyUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const country = params.get('pais');
    const state = params.get('estado');

    if (country) {
      const option = this.countrySelect.querySelector(`option[value="${CSS.escape(country)}"]`);
      if (option) this.countrySelect.value = country;
    }

    this.updateStateOptions();

    if (state) {
      const option = this.stateSelect.querySelector(`option[value="${CSS.escape(state)}"]`);
      if (option) this.stateSelect.value = state;
    }

    this.updateMapHighlight();
    this.renderCards();
  }

  pushUrl() {
    const params = new URLSearchParams();
    const country = this.countrySelect.value;
    const state = this.stateSelect.value;
    if (country) params.set('pais', country);
    if (state) params.set('estado', state);
    const query = params.toString();
    window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }

  bindEvents() {
    this.countrySelect.addEventListener('change', () => {
      this.updateStateOptions();
      this.clearMapActive();
      this.renderCards();
      this.pushUrl();
    });

    this.stateSelect.addEventListener('change', () => {
      this.updateMapHighlight();
      this.renderCards();
      this.pushUrl();
    });

    this.statePaths.forEach((path) => {
      path.addEventListener('click', (event) => {
        event.preventDefault();
        this.stateSelect.value = path.dataset.state;
        this.updateMapHighlight();
        this.renderCards();
        this.pushUrl();
      });
    });

    // Botão "Ver mais" (delegado, pois o botão é recriado a cada lote).
    this.cardsContainer.addEventListener('click', (event) => {
      const button = event.target.closest('[data-more]');
      if (!button) return;
      event.preventDefault();
      this.renderMore();
    });
  }

  updateStateOptions() {
    const country = this.countrySelect.value;
    const states = this.getStatesForCountry(country);
    const previous = this.stateSelect.value;

    this.stateSelect.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent =
      country === 'Brasil'
        ? this.i18n.select_state || 'Selecione o Estado...'
        : this.i18n.select_region || 'Selecione a Região...';
    this.stateSelect.appendChild(placeholder);

    states.forEach((code) => {
      const option = document.createElement('option');
      option.value = code;
      option.textContent = DistributorFinder.STATE_LABELS[code] || code;
      this.stateSelect.appendChild(option);
    });

    if (previous && states.includes(previous)) {
      this.stateSelect.value = previous;
    }

    this.toggleMap(country === 'Brasil');
  }

  toggleMap(show) {
    if (this.mapContainer) this.mapContainer.style.display = show ? '' : 'none';
    if (this.noMapEl) this.noMapEl.style.display = show ? 'none' : '';
  }

  updateMapHighlight() {
    this.clearMapActive();
    const state = this.stateSelect.value;
    if (!state) return;

    const path = this.querySelector(`.state-path[data-state="${CSS.escape(state)}"]`);
    if (path) path.classList.add('is-active');

    const svgState = this.svgStates.find((el) => el.dataset.uf === state);
    svgState?.classList.add('is-active');
  }

  clearMapActive() {
    this.statePaths.forEach((path) => path.classList.remove('is-active'));
    this.svgStates.forEach((el) => el.classList.remove('is-active'));
  }

  renderCards() {
    const distributors = this.getDistributorsForSelection(
      this.countrySelect.value,
      this.stateSelect.value
    );

    this.cardsContainer.innerHTML = '';
    this.pending = distributors;
    this.renderedCount = 0;

    if (!distributors.length) {
      this.cardsContainer.innerHTML = `<p class="distributor-finder__empty">${this.escape(
        this.i18n.empty || 'Nenhum distribuidor encontrado para a seleção.'
      )}</p>`;
      return;
    }

    this.renderMore();
  }

  // Renderiza o próximo lote de cartões e, se ainda houver, o botão "Ver mais".
  renderMore() {
    const oldButton = this.cardsContainer.querySelector('[data-more]');
    if (oldButton) oldButton.remove();

    const batch = this.pending.slice(
      this.renderedCount,
      this.renderedCount + DistributorFinder.BATCH_SIZE
    );

    const fragment = document.createDocumentFragment();
    batch.forEach((entry, index) => {
      fragment.appendChild(this.buildCard(entry, index));
    });
    this.cardsContainer.appendChild(fragment);
    this.renderedCount += batch.length;

    const remaining = this.pending.length - this.renderedCount;
    if (remaining > 0) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'distributor-finder__more';
      button.setAttribute('data-more', '');
      button.textContent = `Ver mais distribuidores (${remaining})`;
      this.cardsContainer.appendChild(button);
    }
  }

  buildCard(entry) {
    const card = document.createElement('div');
    card.className = 'distributor-card';

    const tags = [];
    if (entry.automation === 'Sim') {
      tags.push({ label: this.i18n.tag_automation || 'Automação', modifier: 'automacao' });
    }
    if (entry.components === 'Sim') {
      tags.push({ label: this.i18n.tag_components || 'Componentes', modifier: 'componentes' });
    }

    const address = [entry.address, entry.zip].filter(Boolean).join(' – ');
    const emails = [entry.email, entry.email_secondary].filter(Boolean);

    card.innerHTML = `
      <div class="distributor-card__info">
        <p class="distributor-card__name">${this.escape(entry.company)}</p>
        ${entry.city ? `<p class="distributor-card__city">${this.escape(entry.city)}</p>` : ''}
        ${
          address
            ? `<div class="distributor-card__detail">${this.iconLocation()}<span>${this.escape(address)}</span></div>`
            : ''
        }
        ${
          entry.phone
            ? `<div class="distributor-card__detail">${this.iconPhone()}<a href="tel:${this.escape(entry.phone)}">${this.escape(entry.phone)}</a></div>`
            : ''
        }
        ${emails
          .map(
            (email) =>
              `<div class="distributor-card__detail">${this.iconEmail()}<a href="mailto:${this.escape(email)}">${this.escape(email)}</a></div>`
          )
          .join('')}
        ${
          entry.website
            ? `<div class="distributor-card__detail">${this.iconWeb()}<a href="${this.buildUrl(entry.website)}" target="_blank" rel="noopener">${this.escape(entry.website)}</a></div>`
            : ''
        }
      </div>
      ${
        tags.length
          ? `<div class="distributor-card__tags">${tags
              .map(
                (tag) =>
                  `<span class="distributor-card__tag distributor-card__tag--${tag.modifier}">${this.escape(tag.label)}</span>`
              )
              .join('')}</div>`
          : ''
      }
    `;

    return card;
  }

  buildUrl(site) {
    if (!site) return '#';
    return site.startsWith('http') ? site : `https://${site}`;
  }

  escape(value) {
    if (!value) return '';
    return String(value)
      .replace(/&amp;/g, '&')
      .replace(/&nbsp;/g, ' ')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  iconLocation() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';
  }

  iconPhone() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>';
  }

  iconEmail() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>';
  }

  iconWeb() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';
  }
}

customElements.define('distributor-finder', DistributorFinder);
